import { type FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type { ParentType } from "@/graphql.ts";
import { loadMinimalParentTypes } from "@/redux/modules/sample.ts";
import { createTableSpecForParentTypeParent, createUniqueKeyFromTableSpec } from "@/redux/modules/table-data.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE, PARENT_TABLE_COLUMNS, VIEW } from "@/settings/settings.ts";
import ButtonHeader from "@/ui-modules/core/ButtonHeader.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import PageHeader from "@/ui-modules/core/navigation-panel/PageHeader.tsx";

interface ParentsViewProps {
  type: string;
  subType: string;
  projectId?: number;
  tagIds?: number[];
  keepProjectIcon?: boolean;
}

export const ParentsView: FC<ParentsViewProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [parentTypes, setParentTypes] = useState<ParentType[]>([]);
  const [selectedParentTypeId, setSelectedParentTypeId] = useState<number>();

  const viewId = useURLViewID();

  // load parent types on mount in order to translate the subType to a parentType ID (required for tableSpec)
  useEffect(() => {
    void (async () => {
      const parentTypes = await dispatch(loadMinimalParentTypes());
      setParentTypes(parentTypes);
    })();
  }, []);

  // whenever the parent type selection changes, update the selectedParentTypeId used for the tableSpec
  useEffect(() => {
    if (parentTypes.length > 0) {
      const parentType = parentTypes.find((p) => p.name === props.subType);
      setSelectedParentTypeId(parentType?.id);
    }
  }, [parentTypes, props.subType]);

  const parentsTableSpec = useMemo(
    () => createTableSpecForParentTypeParent(ENTITY_TYPE.SAMPLE_PARENT, selectedParentTypeId, props.projectId),
    [selectedParentTypeId, props.projectId],
  );
  const parentsTableInitOptions = useMemo(
    () => ({
      sortExpression: `-${PARENT_TABLE_COLUMNS.CREATED}`,
    }),
    [],
  );
  const key = useMemo(() => createUniqueKeyFromTableSpec(parentsTableSpec), [parentsTableSpec]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <PageHeader
        title={props.subType ? pluralizeWord(props.subType) : "Parents"}
        setDocumentTitle
        //badge={getCount()}
        actionWidgets={[
          <ButtonHeader
            viewType={VIEW.TABLE_VIEW.type}
            subViewType={props.subType}
            tableSpec={parentsTableSpec}
            hideBlastBtn={true}
            key={"parents-page-buttons-1"}
          />,
        ]}
      />
      {!!selectedParentTypeId && (
        <div style={{ height: "calc(100% - 50px)", overflow: "auto" }}>
          <DataTableContainer
            key={key}
            tableSpec={parentsTableSpec}
            hideCheckboxes={false}
            tableInitializationOptions={parentsTableInitOptions}
            viewId={viewId as number}
          />
        </div>
      )}
    </div>
  );
};
